import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';

import { Observable, of, Subscription } from 'rxjs';
import { finalize, shareReplay, take } from 'rxjs/operators';
import { map } from 'rxjs/operators';

import { ContentItem, DataProcessingService, LanguageService, LoadingObject, LoadingService, MediaService } from '@frontend/common';
import { Topic } from './topic.model'
import { IModelsToContentItemsConverter, Image, ImageRole, Link, Tag } from '@frontend/core';
import { Icon, Review } from '@frontend/shared';

interface TopicFromBackend {
  id: number, //4
  name: string, // "Intercultural Pendulum
  description: string, //"The IC Pendulum helps coaches get faster,....<a href=\\\"https:\\\/\\\/www.icp
  meta: any, // {"published_at":"2023-01-28 13:00:03"}
  category: string, //"interculturalist"
  type: string,
  weight: number,
  icon: Icon,
  slug: string, //"icpendulum"
  links: Link[],
  images: Image[], //[{"id":33,"medially_type":"App\\Models\\Topic","medially_id":4,"file_url":"https:\/\/res.cloudinary.com\/cebt\/image\/upload\/v1674243951\/concepts\/meditating-woman-sitting-in-pose-of-lotus-against-clear-sky-outdoors-SBI-300731584_ie8o5r.jpg","file_name":"concepts\/meditating-woman-sitting-in-pose-of-lotus-against-clear-sky-outdoors-SBI-300731584_ie8o5r.jpg","file_type":"image","size":5250000,"uploader_id":1,"description":"Woman sits cross-legged, apparently meditating at sunset","credits":"Storyblocks","permission":"","category":"thumbnail","type":"main","created_at":"2023-02-06T18:47:23.000000Z","updated_at":"2023-02-06T18:47:23.000000Z"}]
  reviews: Review[],
  created_at: '2023-01-07 13:00:03',
  updated_at: '2023-01-07 13:00:03',
}


@Injectable({
  providedIn: 'root'
})
export class TopicService implements IModelsToContentItemsConverter {

  // Locale-keyed caches of translated topics: one list per UI language, held in
  // parallel and never flushed on locale change, so switching back to a
  // previously-viewed language is instant and free. `_standaloneTopicsByLocale`
  // holds topics fetched individually by slug (backend "lite" topics) that are not
  // yet part of a bulk collection response for that locale.
  private _topicsByLocale = signal<Record<string, Topic[]>>({});
  private _standaloneTopicsByLocale = signal<Record<string, Topic[]>>({});

  /** Bulk topics for the currently-active UI language (empty until fetched). */
  readonly activeTopics = computed<Topic[]>(
    () => this._topicsByLocale()[this.activeLanguageKey] ?? [],
  );
  /** Individually-fetched topics for the currently-active UI language. */
  readonly activeStandaloneTopics = computed<Topic[]>(
    () => this._standaloneTopicsByLocale()[this.activeLanguageKey] ?? [],
  );

  // Active-locale slices as observables, for reactive consumers (toSignal / pipe).
  public readonly topics$: Observable<Topic[]>;
  public readonly standaloneTopics$: Observable<Topic[]>;

  // In-flight bulk requests keyed `locale::category::type`, so service repopulation
  // and a component's own refetch on the same locale change share one HTTP call.
  private activeTopicRequests: { [key: string]: Observable<Topic[]> } = {};
  // Distinct plain topic filters (`category::type`) ever requested, replayed into a
  // newly-active locale so reactive `topics$` consumers stay populated.
  private _requestedFilters = new Set<string>();
  // Locales for which the full, unfiltered topic set has been received.
  private _allTopicsReceivedLocales = new Set<string>();

  activeLanguageSubscription: Subscription;

  private _loadingObject = signal<LoadingObject>({});  // In the legacy application, loadingService caused a circular dependency error.
  private loadingService = inject(LoadingService);


  /** Bulk topics for the currently-active UI language. */
  get topics(): Topic[]{
      return this.activeTopics();
  }
  private get activeLanguageKey(): string {
      return this.languageService.activeLanguage().languageKey;
  }
  get loadingObject(): { [key: string]: boolean } {
      return this._loadingObject();
  }
  public get loadingObjectSignal() : WritableSignal<LoadingObject> {
      return this._loadingObject;
  }
  isAnythingLoading(): boolean {
      return this.loadingService.isAnythingLoading(this._loadingObject());
  }

  setLoadingStatus(key: string, status: boolean): void {
      this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  constructor(
    private languageService: LanguageService,
    private http: HttpClient,
    private mediaService : MediaService,
    private dataProcessingService : DataProcessingService,
  )
  {
    this.topics$ = toObservable(this.activeTopics);
    this.standaloneTopics$ = toObservable(this.activeStandaloneTopics);
    // On a genuine locale change, keep the active locale populated by replaying the
    // topic filters already requested — but only if the feature is in use. Other
    // locales are left intact (never flushed), so switching back is instant and free.
    this.activeLanguageSubscription =
      this.languageService.activeLanguageChanges$.subscribe(() => this.repopulateActiveLocale());
  }

  private filterKey(category: string | null, type: string | null): string {
    return `${category ?? ''}::${type ?? ''}`;
  }

  /**
   * Keep the newly-active locale populated by replaying every plain topic filter
   * requested so far. Runs only when the feature is already in use (some locale
   * cached); a filter already present for the active locale returns from cache with
   * no HTTP, so switching back to a viewed locale is free.
   */
  private repopulateActiveLocale(): void {
    if (
      Object.keys(this._topicsByLocale()).length === 0 &&
      Object.keys(this._standaloneTopicsByLocale()).length === 0
    ) {
      return;
    }
    this._requestedFilters.forEach((fk) => {
      const [category, type] = fk.split('::').map((part) => (part === '' ? null : part));
      this.requestTopics(category, type, false).subscribe({ error: () => void 0 });
    });
  }

  // getPlaceholder () { // generate a dummy Topic

  //   let topic : Topic = {
  //     id: 100000001,
  //     name : 'Dolore ex consequat ipsum aute ad',
  //     description: '<p>Sit ut dolor pariatur irure ex. Aliquip dolore qui nulla est sunt ad cillum eiusmod duis esse magna do aute. <a href="http://www.cultureconnector.com">cco online</a></p>',
  //     slug: JSON.stringify('this-is-the-topic-slug'+Math.random()*1000),
  //     media : null, // get this from the media.service
  //     reviews: null,
  //     created_at: '2023-01-07 13:00:03',
  //     updated_at: '2023-01-07 13:00:03',
  //   };
  //   return topic;
  // };
  transformTopic (topicResponse : TopicFromBackend){
    let topic : Topic = topicResponse;
    return topic;
  }
  getTopic (slug : string, freshFromBackend : boolean){

    // At the moment it seems that a 'lite' topic (backend TopicLiteResource) is adequate (missing links, but we don't use them yet)

    let cachedTopic : Topic;
    if (!freshFromBackend){
      cachedTopic = this.getCachedTopic(slug);
    }
    if (cachedTopic){
      return of(cachedTopic);
    };

    return this.http.get<{'data' : TopicFromBackend}>(
      'api/v1/topics/'+slug)
        .pipe(
          map(response =>{
            let topic : Topic = this.transformTopic(response.data);
            this.cacheTopic(topic,true);
            return topic;
          })
        )
  };
  getCachedTopic (slug: string){
    return this.activeTopics().concat(this.activeStandaloneTopics()).find(t=>t.slug === slug);
  }
  cacheTopic (topic : Topic, asStandaloneTopic : boolean = false){
    const key = this.activeLanguageKey;
    const bucket = asStandaloneTopic ? this._standaloneTopicsByLocale : this._topicsByLocale;
    const targetArray = [...(bucket()[key] ?? [])];
    let cachedTopicIndex = targetArray.findIndex(t=>t.id === topic.id);
    if (cachedTopicIndex >-1){
      targetArray[cachedTopicIndex] = topic;
    } else {
      targetArray.push(topic);
    }
    bucket.update((byLocale) => ({ ...byLocale, [key]: targetArray }));
  };
  cacheTopics(topics:Topic[]){
    const key = this.activeLanguageKey;
    const existing = this._topicsByLocale()[key] ?? [];
    const merged = existing.length
      ? this.dataProcessingService.mergeTwoArraysWithUniqueIdentifiersAvoidingDuplicates(topics,existing,'id')
      : topics;
    this._topicsByLocale.update((byLocale) => ({ ...byLocale, [key]: merged }));
    this.removeStandaloneTopics(topics);
  }
  removeStandaloneTopics(newTopics : Topic[] = []){
    const key = this.activeLanguageKey;
    const existing = this._standaloneTopicsByLocale()[key];
    if (!existing?.length){ return; }
    const newTopicIds = newTopics.map(t=>t.id);
    this._standaloneTopicsByLocale.update((byLocale) => ({
      ...byLocale,
      [key]: existing.filter(topic=>!newTopicIds.includes(topic.id)),
    }));
  }

  private getTopicRequestKey(category:string | null, type:string | null): string {
    return `category:${category ?? 'all'}::type:${type ?? 'all'}`;
  }

  private filterTopics(topics: Topic[], category:string | null, type:string | null): Topic[] {
    let filteredTopics: Topic[] = [];
    if (category){
      filteredTopics = topics.filter(t=> t.category===category);
    }
    if (type){
      const sourceTopics = filteredTopics.length ? filteredTopics : topics;
      filteredTopics = sourceTopics.filter(t=> t.type===type);
    }
    if (!category && !type){
      return topics;
    }
    return filteredTopics;
  }

  getTopicsObservable(category:string | null, type:string | null): Observable<Topic[]> {
    return this.topics$.pipe(
      map((topics) => this.filterTopics(topics, category, type))
    );
  }

  requestTopics(category:string | null, type:string | null, freshFromServer: boolean = false): Observable<Topic[]> {
    const key = this.activeLanguageKey;
    this._requestedFilters.add(this.filterKey(category, type));

    if(!category && !type && this._allTopicsReceivedLocales.has(key) && !freshFromServer){
      return of(this.activeTopics());
    }

    const filteredTopics = this.filterTopics(this.activeTopics(), category, type);
    if (filteredTopics.length && !freshFromServer) {
      return of(filteredTopics);
    }

    const loadingKey = this.getTopicRequestKey(category, type);
    const requestKey = `${key}::${loadingKey}`;
    if (!freshFromServer && this.activeTopicRequests[requestKey]) {
      return this.activeTopicRequests[requestKey];
    }

    let url = 'api/v1/topics?';

    let params = new URLSearchParams();

    if (category){
      params.append('category',category.toString())
    }
    if (type){
      params.append('type',type)
    }

    this.setLoadingStatus(loadingKey, true);
    const request$ = this.http.get<{data:Topic[]}>(url+params.toString()).pipe(
      map((response) => {
        const topicsFromResponse = response?.data ?? [];
        this.cacheTopics(topicsFromResponse);
        if(!category && !type){
          this._allTopicsReceivedLocales.add(this.activeLanguageKey);
        }
        return this.filterTopics(this.activeTopics(), category, type);
      }),
      finalize(() => {
        this.setLoadingStatus(loadingKey, false);
        delete this.activeTopicRequests[requestKey];
      }),
      shareReplay(1)
    );

    this.activeTopicRequests[requestKey] = request$;
    return request$;
  }

  getImage (topicImages : Image[], role : ImageRole){
    if (!topicImages?.length){ return null ;};
    return topicImages.find(i=>i.role===role) ?? null;
  }
  convertTopicToTag (topic : Topic, preserveId : boolean, flagTransformations = 'w_32,c_fill,ar_1:1,r_max/'){
    return new Tag(
      preserveId ? topic.id : null,
      topic.name,
      topic.slug,
      'topic',
      topic.icon?.identifier_1 ? 'fa' : topic.icon?.identifier_2 ? 'cloudinary_icon' : '',
      topic.icon?.identifier_1 ?? topic.icon?.identifier_2 ?? '',
      topic.icon?.identifier_2 ? this.mediaService.getIconUrlFromIconIdentifier2(topic.icon.identifier_2,'w_32,c_fill,ar_1:1,r_max/'):null
    )
  }
  convertTopicsToTags (topics : Topic[], preserveId : boolean = false, iconTransformations? : string){
    if(!topics.length){return [];};
    return topics.map(c => this.convertTopicToTag(c,preserveId,iconTransformations));
  }
  getTopics(category:string | null, type:string | null, freshFromServer: boolean = false) {
    return this.requestTopics(category, type, freshFromServer).pipe(
      map((topics) => topics ?? []),
      take(1)
    );
  }
  convertTopicToContentItem(topic: Topic): ContentItem {
    return new ContentItem( // THIS IS VERY SIMPLE - WE NEED TO ADD MORE PROPERTIES
      topic.id,
      topic.name,
      null,
      null,
      null,
      topic.description,
      null,
      null,
      topic.slug,
      null,
      null,
      topic.images ?? [],
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null
    )
  }

  makeContentItems(topics : Topic[]) : ContentItem[] {
    return topics.map(t=>this.convertTopicToContentItem(t));
  }

}
